import { useState, useEffect } from 'react'
import { TenantGuard } from '@/components/TenantGuard'
import { useAuth } from '@/hooks/useApi'
import { useTenantContext } from '@/contexts/TenantContext'
import { apiClient, type Customer } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { DataTable } from '@/components/ui/data-table'
import { Badge } from '@/components/ui/badge'
import { CustomerDetailModal } from '@/components/ui/customer-detail-modal'
import { Search, Phone, Clock, MessageSquare, User, BarChart3 } from 'lucide-react'

export function Customers() {
  const { isSuperAdmin, user } = useAuth()
  const { selectedTenantId } = useTenantContext()
  const activeTenantId = selectedTenantId || user?.tenant_from_group
  
  const [customers, setCustomers] = useState<Customer[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [fetchError, setFetchError] = useState<string | null>(null)
  const [selectedCustomerPhone, setSelectedCustomerPhone] = useState<string | null>(null)
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false)

  const fetchCustomers = async () => {
    if (!activeTenantId) return
    
    try {
      setLoading(true)
      setFetchError(null)
      const response = await apiClient.getCustomers(activeTenantId)
      setCustomers(response.customers || [])
    } catch (err: any) {
      console.error('Failed to fetch customers:', err)
      setFetchError(`Failed to load customers: ${err.message || 'Server error'}`)
      setCustomers([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchCustomers()
  }, [activeTenantId])

  const formatDate = (dateString: string | number) => {
    if (!dateString) return 'Never'
    // Handle both ISO strings and epoch timestamps
    const date = typeof dateString === 'number' ? new Date(dateString * 1000) : new Date(dateString)
    return date.toLocaleDateString()
  }

  const filteredCustomers = customers.filter(customer =>
    !searchTerm || 
    customer.phone.includes(searchTerm) ||
    customer.name?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const handleCustomerClick = (phone: string) => {
    setSelectedCustomerPhone(phone)
    setIsDetailModalOpen(true)
  }

  const handleCustomerUpdated = () => {
    fetchCustomers()
  }

  const customerColumns = [
    {
      key: 'phone' as const,
      header: 'Customer',
      render: (phone: string, row: Customer) => (
        <div 
          className="cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors"
          onClick={() => handleCustomerClick(phone)}
        >
          <div className="font-medium text-blue-600 hover:text-blue-800">{row.name || 'Anonymous'}</div>
          <div className="text-sm text-gray-500 flex items-center gap-1">
            <Phone className="h-3 w-3" />
            <span className="hover:text-blue-600">{decodeURIComponent(phone)}</span>
          </div>
        </div>
      )
    },
    {
      key: 'total_visits' as const,
      header: 'Visits',
      render: (visits: number, row: Customer) => (
        <div className="text-center">
          <div className="font-bold text-blue-600">{visits}</div>
          <div className="text-xs text-gray-500">{row.completed_services} completed</div>
        </div>
      )
    },
    {
      key: 'avg_wait_time' as const,
      header: 'Avg Wait',
      render: (waitTime: number) => (
        <div className="flex items-center gap-1">
          <Clock className="h-3 w-3 text-gray-500" />
          <span>{waitTime}m</span>
        </div>
      )
    },
    {
      key: 'last_visit' as const,
      header: 'Last Visit',
      render: (date: string) => formatDate(date)
    },
    {
      key: 'sms_enabled' as const,
      header: 'Status',
      render: (smsEnabled: boolean, row: Customer) => (
        <div className="space-y-1">
          <Badge variant={smsEnabled ? 'default' : 'secondary'}>
            {smsEnabled ? 'SMS On' : 'SMS Off'}
          </Badge>
          {row.no_shows > 0 && (
            <div>
              <Badge variant="destructive" className="text-xs">
                {row.no_shows} No-shows
              </Badge>
            </div>
          )}
        </div>
      )
    }
  ]

  const totalCustomers = customers.length
  const activeCustomers = customers.filter(c => c.sms_enabled).length
  const avgVisits = customers.length > 0 ? (customers.reduce((sum, c) => sum + c.total_visits, 0) / customers.length).toFixed(1) : '0'
  const totalNoShows = customers.reduce((sum, c) => sum + c.no_shows, 0)

  return (
    <TenantGuard>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Customer Management</h1>
          <p className="text-gray-600">
            Manage customer database {isSuperAdmin && <Badge variant="outline" className="ml-2">Super Admin</Badge>}
          </p>
        </div>

        {/* Customer Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-blue-600" />
                <div>
                  <div className="font-bold text-blue-600">{totalCustomers}</div>
                  <div className="text-xs text-gray-500">Total Customers</div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <MessageSquare className="h-4 w-4 text-green-600" />
                <div>
                  <div className="font-bold text-green-600">{activeCustomers}</div>
                  <div className="text-xs text-gray-500">SMS Active</div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <BarChart3 className="h-4 w-4 text-purple-600" />
                <div>
                  <div className="font-bold text-purple-600">{avgVisits}</div>
                  <div className="text-xs text-gray-500">Avg Visits</div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-red-600" />
                <div>
                  <div className="font-bold text-red-600">{totalNoShows}</div>
                  <div className="text-xs text-gray-500">Total No-shows</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Customer List */}
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <CardTitle>Customer Database</CardTitle>
                <CardDescription>All customers who have used your queues</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Search by phone or name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Button variant="outline" onClick={fetchCustomers}>
                Refresh
              </Button>
            </div>

            {loading ? (
              <div>Loading customers...</div>
            ) : fetchError ? (
              <div className="text-center py-8">
                <div className="text-red-600 mb-2">{fetchError}</div>
                <Button variant="outline" onClick={fetchCustomers}>
                  Try Again
                </Button>
              </div>
            ) : (
              <DataTable
                data={filteredCustomers}
                columns={customerColumns}
                searchable={false}
              />
            )}
          </CardContent>
        </Card>

        {/* Customer Detail Modal */}
        {selectedCustomerPhone && (
          <CustomerDetailModal
            isOpen={isDetailModalOpen}
            onClose={() => {
              setIsDetailModalOpen(false)
              setSelectedCustomerPhone(null)
            }}
            tenantId={activeTenantId!}
            customerPhone={selectedCustomerPhone}
            onCustomerUpdated={handleCustomerUpdated}
          />
        )}


      </div>
    </TenantGuard>
  )
}